<script lang="ts">
  import { api } from '$shared/api';
  import { CREATOR_TRACK_SUBMISSION } from '$lib/features';
  import { addToast } from '$shared/toasts.svelte';
  import LoadingSkeleton from '$shared/LoadingSkeleton.svelte';
  import { youtubeThumbUrl } from '$lib/utils/youtube';
  import type { CatalogTrackMine } from '$types/index';

  let tracks = $state<CatalogTrackMine[]>([]);
  let loading = $state(true);
  let loadFailed = $state(false);
  let expandedIds = $state<Set<number>>(new Set());

  const songCount = $derived(tracks.length);
  const inDuelCount = $derived(tracks.reduce((n, t) => n + (t.active_duel_count || 0), 0));
  const winCount = $derived(tracks.reduce((n, t) => n + (t.win_count || 0), 0));

  $effect(() => {
    loadCatalog();
  });

  async function loadCatalog() {
    try {
      // Tolerate both a bare array and a `{tracks: [...]}` wrapper — the
      // contract documents the row shape only.
      const res = await api.get<CatalogTrackMine[] | { tracks: CatalogTrackMine[] }>('/users/current/catalog');
      tracks = Array.isArray(res) ? res : (res?.tracks ?? []);
    } catch (e) {
      console.error('Failed to load catalog:', e);
      loadFailed = true;
    } finally {
      loading = false;
    }
  }

  function toggleExpanded(id: number) {
    const next = new Set(expandedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    expandedIds = next;
  }

  async function removeTrack(track: CatalogTrackMine) {
    if (!confirm(`Remove "${track.title}" from the catalog?`)) return;
    // Optimistic remove — restore the list and toast on failure.
    const prior = tracks;
    tracks = tracks.filter((t) => t.id !== track.id);
    try {
      await api.delete(`/catalog/tracks/${track.id}`);
      addToast('Song removed from the catalog', 'join');
    } catch (err) {
      tracks = prior;
      addToast((err as Error).message || 'Could not remove that song.', 'error');
    }
  }

  function duelHref(duel: { id: number; status: string }): string {
    return duel.status === 'ended' ? `#/duels/${duel.id}/result` : `#/duels/${duel.id}`;
  }
</script>

<div>
  <div class="page-head">
    <h1>My Catalog</h1>
    {#if CREATOR_TRACK_SUBMISSION}
      <a href="#/catalog/add" class="btn btn-primary">+ Add a song</a>
    {/if}
  </div>

  {#if loading}
    <LoadingSkeleton lines={5} />
  {:else if loadFailed}
    <p class="load-error">Could not load your catalog. Reload to try again.</p>
  {:else}
    <div class="grid-stats summary">
      {#each [{ label: 'Songs', value: songCount }, { label: 'In duels', value: inDuelCount }, { label: 'Wins', value: winCount }] as card}
        <div class="stat-card">
          <span class="stat-value">{card.value}</span>
          <span class="stat-label">{card.label}</span>
        </div>
      {/each}
    </div>

    {#if tracks.length === 0}
      <div class="empty-state">
        {#if CREATOR_TRACK_SUBMISSION}
          <p class="empty-state__lead">Your songs compete for you.</p>
          <p class="empty-state__body">
            Add a song to the catalog — players claim it into themed duels, the crowd votes, and you collect creator
            crowns.
          </p>
          <a href="#/catalog/add" class="btn btn-primary">+ Add a song</a>
        {:else}
          <p class="empty-state__lead">No songs in your catalog.</p>
          <p class="empty-state__body">Adding songs isn't open yet.</p>
        {/if}
      </div>
    {:else}
      <div class="catalog-list">
        {#each tracks as track (track.id)}
          <div class="catalog-row">
            <div class="catalog-row__main">
              <a href="#/tracks/{track.id}" aria-label="View {track.title}">
                <img class="catalog-thumb" src={youtubeThumbUrl(track.youtube_id)} alt="" loading="lazy" />
              </a>
              <div class="catalog-info">
                <div class="catalog-header">
                  <a href="#/tracks/{track.id}" class="catalog-title">{track.title}</a>
                  {#if track.win_count > 0}
                    <span class="chip-status chip-status--gold"
                      >{track.win_count} win{track.win_count === 1 ? '' : 's'}</span
                    >
                  {:else if track.active_duel_count > 0}
                    <span class="chip-status chip-status--live"
                      >In {track.active_duel_count} duel{track.active_duel_count === 1 ? '' : 's'}</span
                    >
                  {:else}
                    <span class="chip-status">In catalog</span>
                  {/if}
                </div>
                <div class="catalog-meta">{track.genre_name} &middot; {track.style}</div>
                {#if track.duels && track.duels.length > 0}
                  <button type="button" class="btn-expand" onclick={() => toggleExpanded(track.id)}>
                    {expandedIds.has(track.id) ? 'Hide' : 'Show'}
                    {track.duels.length} duel{track.duels.length === 1 ? '' : 's'}
                    <span class="btn-expand__chev" class:btn-expand__chev--open={expandedIds.has(track.id)}
                      >&#9662;</span
                    >
                  </button>
                {/if}
              </div>
              {#if track.deletable}
                <button
                  type="button"
                  class="btn-delete"
                  aria-label="Remove {track.title} from the catalog"
                  title="Remove from catalog"
                  onclick={() => removeTrack(track)}
                >
                  <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                </button>
              {/if}
            </div>
            {#if expandedIds.has(track.id) && track.duels && track.duels.length > 0}
              <ul class="duel-sublist">
                {#each track.duels as duel (duel.id)}
                  <li class="duel-subrow">
                    <a href={duelHref(duel)} class="duel-subrow__name">{duel.name}</a>
                    <span class="duel-subrow__status">{duel.status}</span>
                    {#if duel.my_result}
                      <span class="duel-subrow__result">{duel.my_result}</span>
                    {/if}
                  </li>
                {/each}
              </ul>
            {/if}
          </div>
        {/each}
      </div>
    {/if}
  {/if}
</div>

<style>
  .page-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    flex-wrap: wrap;
    margin-bottom: 1.5rem;
  }
  .page-head h1 {
    margin: 0;
  }

  .summary {
    margin-bottom: 1.5rem;
  }
  .stat-card {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    padding: 0.75rem;
    text-align: center;
  }
  .stat-value {
    display: block;
    font-size: 1.25rem;
    font-weight: 700;
    color: var(--cyan);
  }
  .stat-label {
    font-size: 0.75rem;
    color: var(--text-secondary);
  }

  .load-error {
    color: var(--error);
  }

  .empty-state {
    padding: 2rem 1.5rem;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    text-align: center;
  }
  .empty-state__lead {
    font-size: 1.1rem;
    font-weight: 700;
    margin: 0 0 0.5rem;
    background: linear-gradient(135deg, var(--pink), var(--purple));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  .empty-state__body {
    color: var(--text-secondary);
    font-size: 0.9rem;
    max-width: 32rem;
    margin: 0 auto 1.25rem;
  }

  .catalog-list {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }
  .catalog-row {
    padding: 0.75rem;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius);
  }
  .catalog-row__main {
    display: flex;
    gap: 0.75rem;
    align-items: flex-start;
  }
  .catalog-thumb {
    width: 80px;
    height: 45px;
    object-fit: cover;
    border-radius: var(--radius-sm);
    border: 1px solid var(--border);
    flex-shrink: 0;
  }
  .catalog-info {
    flex: 1;
    min-width: 0;
  }
  .catalog-header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-wrap: wrap;
  }
  .catalog-title {
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 100%;
  }
  .catalog-title:hover {
    color: var(--pink);
  }
  .catalog-meta {
    margin-top: 0.25rem;
    font-size: 0.875rem;
    color: var(--text-secondary);
  }

  .chip-status {
    flex-shrink: 0;
    font-size: 0.7rem;
    font-weight: 600;
    letter-spacing: 0.3px;
    padding: 0.15rem 0.5rem;
    border-radius: 999px;
    border: 1px solid var(--border);
    color: var(--text-muted);
    white-space: nowrap;
  }
  .chip-status--live {
    color: var(--cyan);
    border-color: rgba(0, 229, 255, 0.4);
    background: rgba(0, 229, 255, 0.08);
  }
  .chip-status--gold {
    color: var(--gold, #ffd700);
    border-color: rgba(255, 215, 0, 0.4);
    background: rgba(255, 215, 0, 0.08);
  }

  .btn-expand {
    margin-top: 0.35rem;
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    background: none;
    border: none;
    padding: 0;
    font-size: 0.75rem;
    color: var(--text-muted);
    cursor: pointer;
  }
  .btn-expand:hover {
    color: var(--cyan);
  }
  .btn-expand__chev {
    display: inline-block;
    transition: transform 0.15s;
  }
  .btn-expand__chev--open {
    transform: rotate(180deg);
  }

  .duel-sublist {
    list-style: none;
    margin: 0.75rem 0 0;
    padding: 0.5rem 0 0;
    border-top: 1px solid var(--border);
  }
  .duel-subrow {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.35rem 0;
    font-size: 0.85rem;
  }
  .duel-subrow__name {
    color: var(--text);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .duel-subrow__name:hover {
    color: var(--cyan);
  }
  .duel-subrow__status {
    flex-shrink: 0;
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: var(--text-muted);
  }
  .duel-subrow__result {
    flex-shrink: 0;
    font-size: 0.75rem;
    color: var(--text-secondary);
  }

  .btn-delete {
    flex-shrink: 0;
    background: none;
    border: none;
    color: var(--text-muted);
    cursor: pointer;
    padding: 0.25rem;
    border-radius: var(--radius-sm);
  }
  .btn-delete:hover {
    color: var(--error);
    background: rgba(255, 45, 120, 0.1);
  }
</style>
